import { supportsOfflinePos } from '../runtime/frontofficeRuntime';
import type {
  CupsPrinterInfo,
  ReceiptPaperSize,
  ReceiptPrinterConfig,
  ThermalReceiptData,
} from './thermalReceiptTypes';
import { RECEIPT_PRINTER_NOT_CONFIGURED } from './thermalReceiptTypes';

export type ReceiptPrintResult =
  | { ok: true; printer?: string }
  | { ok: false; code: string; error: string };

function getIpcRenderer():
  | { invoke(channel: string, ...args: unknown[]): Promise<unknown> }
  | null {
  try {
    const w = window as unknown as { require?: NodeRequire };
    if (typeof w.require !== 'function') return null;
    const electron = w.require('electron') as {
      ipcRenderer?: { invoke(channel: string, ...args: unknown[]): Promise<unknown> };
    };
    return electron.ipcRenderer ?? null;
  } catch {
    return null;
  }
}

function electronIpcAvailable(): boolean {
  return supportsOfflinePos() && !!getIpcRenderer();
}

export async function printThermalReceipt(
  data: ThermalReceiptData,
  printerName?: string,
  paperSize?: ReceiptPaperSize,
): Promise<ReceiptPrintResult> {
  if (!electronIpcAvailable()) {
    return {
      ok: false,
      code: 'ELECTRON_REQUIRED',
      error: 'ELECTRON_REQUIRED',
    };
  }

  const ipc = getIpcRenderer()!;
  try {
    const payload = {
      data,
      printerName: printerName?.trim() || undefined,
      paperSize,
    };
    const result = (await ipc.invoke('receipt:print', payload)) as ReceiptPrintResult;
    return result ?? { ok: false, code: 'INVALID_RESPONSE', error: 'INVALID_RESPONSE' };
  } catch (error) {
    return {
      ok: false,
      code: 'PRINT_FAILED',
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

export async function getReceiptPrinterConfigLocal(): Promise<ReceiptPrinterConfig | null> {
  if (!electronIpcAvailable()) return null;
  const ipc = getIpcRenderer()!;
  try {
    return (await ipc.invoke('receipt:printer-config')) as ReceiptPrinterConfig | null;
  } catch {
    return null;
  }
}

export async function listReceiptPrintersLocal(): Promise<CupsPrinterInfo[]> {
  if (!electronIpcAvailable()) return [];
  const ipc = getIpcRenderer()!;
  try {
    return ((await ipc.invoke('receipt:list-printers')) as CupsPrinterInfo[]) ?? [];
  } catch {
    return [];
  }
}

export async function setReceiptPrinterConfigLocal(
  printerName: string,
  configuredBy: string,
): Promise<ReceiptPrinterConfig | null> {
  if (!electronIpcAvailable()) return null;
  const ipc = getIpcRenderer()!;
  try {
    return (await ipc.invoke('receipt:set-config', {
      printerName,
      configuredBy,
    })) as ReceiptPrinterConfig;
  } catch {
    return null;
  }
}

/** Electron: TSPL sample label via local winspool/CUPS (Windows queues not on API host). */
export async function printBarcodeTestLocal(
  printerName: string,
  content?: { includeProductName?: boolean; includePriceTtc?: boolean },
): Promise<ReceiptPrintResult> {
  if (!electronIpcAvailable()) {
    return {
      ok: false,
      code: 'ELECTRON_REQUIRED',
      error: 'ELECTRON_REQUIRED',
    };
  }
  const ipc = getIpcRenderer()!;
  try {
    const result = (await ipc.invoke('barcode:print-test', {
      printerName,
      includeProductName: content?.includeProductName,
      includePriceTtc: content?.includePriceTtc,
    })) as ReceiptPrintResult;
    return result ?? { ok: false, code: 'INVALID_RESPONSE', error: 'INVALID_RESPONSE' };
  } catch (error) {
    return {
      ok: false,
      code: 'PRINT_FAILED',
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

export { RECEIPT_PRINTER_NOT_CONFIGURED };
